// The `ignoreMutation` every React node view with content (`NodeViewContent`)
// must pass. Without it, inserting such a node on an iPhone / iPad (and
// Android) froze the page: choosing a callout from the slash menu on a phone
// took the whole app down.
//
// Why: each render of a React node view makes three childList mutations
// (recorded with a MutationObserver):
//   1. the `NodeViewWrapper` is added to the node view's root (the
//      `ReactRenderer` element);
//   2. the contentDOM is removed from the root (`@tiptap/react` first puts it
//      there, then moves it once `NodeViewContent` has rendered its ref);
//   3. the contentDOM is added to the `NodeViewContent` element.
// TipTap's default `ignoreMutation`, on iOS / Android while the editor is
// focused, lets ProseMirror read childList changes inside a node view whose
// added / removed nodes are all contenteditable (to catch the soft keyboard
// deleting whole elements). The wrapper and the contentDOM inherit
// contenteditable, so the three above match: ProseMirror re-reads the DOM,
// replaces the node, the node view is rebuilt, React makes three more... and it
// never ends. Desktops ignore them by default, so it never happened there.
//
// Here the three mutations made by React / `@tiptap/react` are ignored and the
// rest is judged like the default (the keyboard deleting content inside still
// reaches ProseMirror). They are told apart by the marks `@tiptap/react` puts
// on its elements: `data-node-view-wrapper` on the wrapper,
// `data-node-view-content-react` on the contentDOM and `data-node-view-content`
// on the `NodeViewContent` element.
import {
  type Editor,
  isAndroid,
  isiOS,
  type NodeViewRendererOptions,
} from "@tiptap/core";

type IgnoreMutation = NonNullable<NodeViewRendererOptions["ignoreMutation"]>;

export function ignoreMutationForReactNodeView(editor: Editor): IgnoreMutation {
  return ({ mutation }) => {
    if (mutation.type === "selection") return false;
    const record = mutation as MutationRecord;
    const target =
      record.target instanceof Element
        ? record.target
        : record.target.parentElement;
    const content = target?.closest("[data-node-view-content-react]") ?? null;
    if (content) {
      // As the default: the contentDOM's own attributes are ignored, changes
      // inside it are read.
      return record.type === "attributes" && target === content;
    }
    if (record.type === "childList") {
      const added = Array.from(record.addedNodes);
      const removed = Array.from(record.removedNodes);
      // 1 and 3: only putting the wrapper or the contentDOM in place.
      if (removed.length === 0 && added.every(isReactChrome)) return true;
      // 2: taking the contentDOM out before it moves. Not a removal from
      // inside `NodeViewContent` (that one may be the keyboard deleting, so it
      // is left to the default below).
      if (
        added.length === 0 &&
        removed.every(isReactContentDom) &&
        !target?.hasAttribute("data-node-view-content")
      ) {
        return true;
      }
      if ((isiOS() || isAndroid()) && editor.isFocused) {
        const changed = [...added, ...removed];
        if (changed.every((n) => (n as HTMLElement).isContentEditable)) {
          return false;
        }
      }
    }
    return true;
  };
}

function isReactContentDom(node: Node): boolean {
  return (
    node instanceof HTMLElement &&
    node.hasAttribute("data-node-view-content-react")
  );
}

function isReactChrome(node: Node): boolean {
  return (
    node instanceof HTMLElement &&
    (node.hasAttribute("data-node-view-wrapper") || isReactContentDom(node))
  );
}
